import { useEffect, useState } from "react";
import "../Privacy/Privacy";
import { Helmet } from "react-helmet-async";

import LandingNavbar from "../../components/LandingNavbar/LandingNavbar";
import TopBanner from "../../components/TopBanner/TopBanner";
import Footer from "../../components/Footer/Footer";

const Terms = () => {
  const [activeSection, setActiveSection] = useState("introduction");

  useEffect(() => {
    const sections = document.querySelectorAll(".privacy-section");

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleSection = entries.find(
          (entry) => entry.isIntersecting
        );

        if (visibleSection) {
          setActiveSection(visibleSection.target.id);
        }
      },
      {
        rootMargin: "-15% 0px -65% 0px",
        threshold: 0,
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

 return (
  <main className="privacy-page">

    <Helmet>
      <title>Terms and Conditions | Xyncely</title>

      <meta
        name="description"
        content="Review the terms governing access to and use of the Xyncely website, products and services."
      />

      <meta name="robots" content="noindex, follow" />

      <link
        rel="canonical"
        href="https://xyncely.com/terms"
      />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Xyncely" />
      <meta property="og:locale" content="en_IN" />

      <meta
        property="og:title"
        content="Terms and Conditions | Xyncely"
      />

      <meta
        property="og:description"
        content="Review the terms governing access to and use of the Xyncely website, products and services."
      />

      <meta
        property="og:url"
        content="https://xyncely.com/terms"
      />

      <meta
        property="og:image"
        content="https://xyncely.com/assets/xyncely-og-1200x630.png"
      />

      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta
        property="og:image:alt"
        content="Xyncely - The Command Center for Influencer Marketing"
      />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />

      <meta
        name="twitter:title"
        content="Terms and Conditions | Xyncely"
      />

      <meta
        name="twitter:description"
        content="Review the terms governing access to and use of the Xyncely website, products and services."
      />

      <meta
        name="twitter:image"
        content="https://xyncely.com/assets/xyncely-og-1200x630.png"
      />

      <meta
        name="twitter:image:alt"
        content="Xyncely - The Command Center for Influencer Marketing"
      />
    </Helmet>

    <TopBanner />
    <LandingNavbar />

      {/* =========================
          Back To Home
      ========================= */}

      <div className="privacy-page__back">
        <a href="/">
          ← <span>Back to Home</span>
        </a>
      </div>

      {/* =========================
          Hero
      ========================= */}

      <section className="privacy-hero">
        <div className="privacy-hero__content">
          <h1>
            Terms &
            <br />
            Conditions.
          </h1>

          <p>
            Please read these terms carefully before accessing or using
            the Xyncely website, application, or services.
          </p>
        </div>
      </section>

      {/* =========================
          Terms Document
      ========================= */}

      <section className="privacy-document">

        <div className="privacy-document__meta">
          <span>
            EFFECTIVE DATE&nbsp;&nbsp; December 1, 2023
          </span>
        </div>

        <div className="privacy-layout">

          {/* =========================
              Sidebar
          ========================= */}

          <aside className="privacy-sidebar">

            <span className="privacy-sidebar__title">
              ON THIS PAGE
            </span>

            <a
              href="#introduction"
              className={
                activeSection === "introduction" ? "active" : ""
              }
            >
              Introduction
            </a>

            <a
              href="#definition"
              className={
                activeSection === "definition" ? "active" : ""
              }
            >
              Definition
            </a>

            <a
              href="#right-to-use"
              className={
                activeSection === "right-to-use" ? "active" : ""
              }
            >
              Right to Use
            </a>

            <a
              href="#intellectual-property"
              className={
                activeSection === "intellectual-property" ? "active" : ""
              }
            >
              Intellectual Property
            </a>

            <a
              href="#user-accounts"
              className={
                activeSection === "user-accounts" ? "active" : ""
              }
            >
              User Accounts
            </a>

            <a
              href="#use-of-platform"
              className={
                activeSection === "use-of-platform" ? "active" : ""
              }
            >
              Use of Website
            </a>

            <a
              href="#fees-payment-refund"
              className={
                activeSection === "fees-payment-refund" ? "active" : ""
              }
            >
              Fees, Payment & Refund
            </a>

            <a
              href="#your-content"
              className={
                activeSection === "your-content" ? "active" : ""
              }
            >
              Your Content
            </a>

            <a
              href="#social-handlers"
              className={
                activeSection === "social-handlers" ? "active" : ""
              }
            >
              Social Handlers
            </a>

            <a
              href="#termination"
              className={
                activeSection === "termination" ? "active" : ""
              }
            >
              Termination By Us
            </a>

            <a
              href="#questions"
              className={
                activeSection === "questions" ? "active" : ""
              }
            >
              Questions?
            </a>

            <div className="privacy-sidebar__question">
              <strong>Questions?</strong>

              <p>
                Our team is here to help you understand these
                Terms and Conditions.
              </p>

              <a href="mailto:support@xyncely.com">
                support@xyncely.com ↗
              </a>
            </div>

          </aside>

          {/* =========================
              Main Content
          ========================= */}

          <article className="privacy-content">

            {/* =========================
                Introduction
            ========================= */}

            <section
              id="introduction"
              className="privacy-section"
            >
              <h2>Introduction</h2>

              <p>
                These terms and conditions govern your relationship
                with the “Xyncely” (referred to as the “Platform”),
                a division of AFFL Xyncely Private Limited in terms
                of its provision of services.
              </p>

              <p>
                The services offered on or through the use of our
                website, app or any related applications (including
                mobile phone applications), or use of any other
                media format for the purposes of promoting the
                Platform, you accept, agree to comply with and be
                bound by this Agreement which forms a binding
                Agreement between you and Platform.
              </p>

              <p>
                By using the Services, you represent and warrant
                that you are of legal age to form a binding contract
                with us.
              </p>

              <p>
                <strong>
                  INSTALLING, SIGNING UP, ACCESSING, BROWSING OR
                  OTHERWISE USING THE WEBSITE AND/OR AN APPLICATION
                  INDICATES YOUR AGREEMENT TO ALL THE TERMS AND
                  CONDITIONS HEREIN.
                </strong>
              </p>

              <p>
                You are requested to read the Terms carefully before
                proceeding. If you do not want to be bound by these
                Terms, you must not retain, access, or use the
                Application and/or the Website in any manner
                whatsoever.
              </p>

              <p>
                Anything done, caused to be done, whether expressly
                or impliedly in contravention of these Terms may
                render you liable for legal action.
              </p>
            </section>

            {/* =========================
                Definition
            ========================= */}

            <section
              id="definition"
              className="privacy-section"
            >
              <h2>Definition</h2>

              <p>
                For the purposes of these Terms and Conditions:
              </p>

              <ol className="privacy-list">
                <li>
                  <strong>“Agreement”</strong> shall mean the Terms,
                  the Privacy Policy, Cancellation and Refund Policy,
                  Pricing Policy and any other policies as may be
                  framed by us from time to time.
                </li>

                <li>
                  <strong>
                    “Xyncely”, “we”, “our”, “us”, “Platform” and
                    “Company”
                  </strong>{" "}
                  shall mean AFFL Xyncely Private Limited having
                  its registered office at New Delhi, India.
                </li>

                <li>
                  <strong>“Platform”</strong> shall mean a collective
                  reference to the Services provided through the
                  website at www.xyncely.com or through the Mobile
                  application installed by you on your mobile device,
                  tablet, computer, etc.
                </li>

                <li>
                  <strong>“Website”</strong> shall mean the website
                  www.xyncely.com of the company.
                </li>

                <li>
                  <strong>
                    “You”, “Your”, “your”, “yourself” and “user”
                  </strong>{" "}
                  shall mean the end user who uses the Platform and
                  mobile application.
                </li>
              </ol>
            </section>

            {/* =========================
                Right To Use
            ========================= */}

            <section
              id="right-to-use"
              className="privacy-section"
            >
              <h2>Right to Use</h2>

              <p>
                Your right to use the Services is subject to the
                limitations, conditions, and restrictions established
                by us from time to time, at our sole discretion.
              </p>

              <p>
                We may alter, suspend, or discontinue the Services
                at any time, without any prior intimation to the
                user.
              </p>

              <p>
                We may also impose limits on certain features and/or
                aspects of the Services and/or restrict your access
                to certain parts of the Services and/or all of the
                Services without any prior intimation and any
                liability on us.
              </p>
            </section>

            {/* =========================
                Intellectual Property
            ========================= */}

            <section
              id="intellectual-property"
              className="privacy-section"
            >
              <h2>Intellectual Property Right</h2>

              <p>
                We solely own rights in the name and mark of Xyncely
                and the logo that appears on the Platform (the
                “Logo”).
              </p>

              <p>
                You are not permitted to, and you agree not to, use
                these marks in any manner, including as part of any
                other trademark, Company name or domain name, in
                connection with any product or service.
              </p>

              <p>
                We own and have the authorized license of all
                intellectual property rights, including copyright,
                in and to the Platform including intellectual
                property rights in the content hosted, published,
                displayed, uploaded on the Platform by us.
              </p>

              <p>
                Copyright laws and treaties throughout the world
                protect these works and all rights in and to them
                are reserved by us.
              </p>

              <p>
                No information, content or material from us may be
                copied, reproduced, republished, uploaded, posted,
                transmitted or distributed in any way without our
                express written permission.
              </p>
            </section>

            {/* =========================
                User Accounts
            ========================= */}

            <section
              id="user-accounts"
              className="privacy-section"
            >
              <h2>User Accounts</h2>

              <p>
                To access the full functionality of the
                Website/Mobile application, you are requested to
                register an account (the “User Account”).
              </p>

              <p>
                By registering the User Account, You agree to:
              </p>

              <ol className="privacy-list">
                <li>
                  provide and keep current, accurate and complete
                  information about Yourself and Your company
                  (“Information”);
                </li>

                <li>
                  maintain the confidentiality of your User Account
                  and log-in credentials, if applicable;
                </li>

                <li>
                  restrict access to the User Account to all others.
                  You agree to accept responsibility for all
                  activities that occur under the User Account.
                </li>

                <li>
                  If We have reasonable grounds to suspect that the
                  Information You provide is inaccurate, incomplete
                  or impersonates another person, we reserve the
                  right to suspend or terminate your User Account.
                  All Information will be stored and used in
                  accordance with our Privacy Policy.
                </li>

                <li>
                  Xyncely will not be liable for any loss or damage
                  caused by any unauthorized use of Your Account.
                </li>

                <li>
                  By creating the User Account and providing details
                  of a business entity, you confirm that You are an
                  authorised employee, contractor, or representative
                  of the business entity. Xyncely shall not be
                  responsible in any manner and bear no liability for
                  Your activities carried out without such
                  authorisation.
                </li>
              </ol>
            </section>

            {/* =========================
                Use Of Website
            ========================= */}

            <section
              id="use-of-platform"
              className="privacy-section"
            >
              <h2>Use of the Website/Mobile Application</h2>

              <p>
                By accessing and using the Website/Mobile
                application, you acknowledge that you are responsible
                for your actions and for all content that you post on
                the Website/Mobile application.
              </p>

              <p>
                You represent and warrant, to the best of your
                knowledge that:
              </p>

              <ol className="privacy-list">
                <li>
                  You will, when posting or providing User Content on
                  the Website, adhere to Our guidelines and bear all
                  liability related to your submission of such User
                  Content through the Website.
                </li>

                <li>
                  You will not post or otherwise provide User Content
                  that is unlawful, defamatory, infringing, libelous,
                  abusive, disparaging, pornographic, invasive of
                  another's privacy, promotes illegal activities/conduct
                  or violates applicable laws or regulations.
                </li>

                <li>
                  You will not post or otherwise provide User Content
                  that You do not have the right to make available
                  under any law or contractual or fiduciary
                  relationship.
                </li>

                <li>
                  You will not post or otherwise provide User Content
                  that contains a software virus or any other code
                  files or programs with the ability to interrupt,
                  destroy, compromise, or otherwise limit the
                  functionality of any computer software or hardware
                  or telecommunications equipment.
                </li>

                <li>
                  You will not forge headers or otherwise manipulate
                  identifiers for the purpose of disguising the origin
                  of any User Content.
                </li>

                <li>
                  You will not frame or utilize framing techniques to
                  enclose any trademark, logo, or other proprietary
                  information of the Website or Our vendors without
                  Our express written consent.
                </li>

                <li>
                  You will not avoid, bypass, reverse engineer,
                  interfere with, deactivate, impair, descramble or
                  otherwise circumvent any technical measure
                  implemented by Us to administer and protect the
                  Website.
                </li>

                <li>
                  You will not use any automated means or form of data
                  scraping or data extraction to access, query,
                  download or otherwise collect any information or
                  content from the Website, except as expressly
                  permitted by Us.
                </li>

                <li>
                  You will not duplicate, download, publish, modify
                  or otherwise distribute Our Content for any purpose
                  other than for your own individual use, except with
                  Our explicit consent hereto.
                </li>

                <li>
                  You will not collect or “harvest” from the Website
                  the personal information of other Users without
                  their consent for the purpose of transmitting
                  unsolicited commercial mass mailings, “spamming”
                  or any other unlawful purpose.
                </li>

                <li>
                  You will not access the Website by any means other
                  than through interfaces expressly authorized by Us
                  and these Terms.
                </li>

                <li>
                  In the event of any actual or suspected breach,
                  your Use of the Platform will be terminated
                  immediately.
                </li>
              </ol>
            </section>

            {/* =========================
                Fees Payment Refund
            ========================= */}

            <section
              id="fees-payment-refund"
              className="privacy-section"
            >
              <h2>Fees, Payment and Refund Policy</h2>

              <h3>Fees</h3>

              <p>
                As consideration for any purchase you make on the
                Website, you shall pay all applicable fees and taxes
                (collectively, the “Fees”).
              </p>

              <p>
                We (or our third-party payment processor) shall
                authorize your credit card, bank account, or other
                approved payment facility that you provided during
                the registration process for the full payment of the
                Fees, and you hereby consent to the same.
              </p>

              <p>
                You take full responsibility for all taxes and fees
                of any nature associated with the Services.
              </p>

              <p>
                You agree to pay platform fee charged by Xyncely for
                using the Site or Mobile application to create
                campaigns and fulfilling the process.
              </p>

              <p>
                The Fees are subject to change without prior notice.
                Any changes to the Fees will be made available on
                the Website.
              </p>

              <h3>Payments</h3>

              <p>
                All payments are processed by our third-party
                payment processors (the “Payment Processors”).
              </p>

              <p>
                When you make a payment, the Payment Processors
                collect some personal data from you which allows them
                to make the payments requested by you, such as your
                credit card number, expiration date, billing address,
                and security codes.
              </p>

              <p>
                The Payment Processors handle all steps in the
                payment process through their systems, including
                data collection and data processing. Xyncely does not
                have direct access to your full payment information.
              </p>

              <p>
                You are responsible for ensuring that all payment
                information is correct and the funds necessary for
                payment are available.
              </p>

              <p>
                You agree not to hold Us liable for payments that do
                not reach Us because You have quoted incorrect payment
                information or the Payment Processors refused the
                payment for any other reason.
              </p>

              <h3>Refund Policy</h3>

              <p>
                At Xyncely, we are not obligated to return any fee
                under any circumstances.
              </p>
            </section>

            {/* =========================
                Your Content
            ========================= */}

            <section
              id="your-content"
              className="privacy-section"
            >
              <h2>Your Content</h2>

              <p>
                You must not infringe any intellectual property
                rights of others when uploading or creating your
                content.
              </p>

              <p>
                Xyncely reserves the right, at its sole discretion,
                to refuse to upload, modify, delete, or remove your
                content, in whole or in part, that violates these
                Terms or may harm the reputation of Xyncely.
              </p>
            </section>

            {/* =========================
                Social Handlers
            ========================= */}

            <section
              id="social-handlers"
              className="privacy-section"
            >
              <h2>Social Handlers</h2>

              <p>
                We use social handlers like Instagram, Facebook,
                Twitter, LinkedIn, YouTube Services. All users
                including Advertiser and Marketer are agreeing to be
                bound by social handler’s Terms of Service (ToS).
              </p>

              <ul className="privacy-list">
                <li>
                  YouTube - https://www.youtube.com/t/terms
                </li>

                <li>
                  Facebook - https://www.facebook.com/terms.php
                </li>

                <li>
                  Instagram - https://help.instagram.com/581066165581870
                </li>

                <li>
                  LinkedIn - https://www.linkedin.com/legal/user-agreement
                </li>

                <li>
                  Twitter - https://twitter.com/en/tos
                </li>
              </ul>
            </section>

            {/* =========================
                Termination
            ========================= */}

            <section
              id="termination"
              className="privacy-section"
            >
              <h2>Termination By Us</h2>

              <p>
                We reserve the right, in our sole discretion,
                without penalty and at any time without notice, to
                modify or discontinue (temporarily or permanently)
                User’s access to the Website, or any part thereof if
                We believe that User is violating these Terms.
              </p>

              <p>
                If the User uses or attempts to use the Website for
                any purpose that contravenes these Terms, including
                without limitation tampering, hacking, data scraping,
                modifying or otherwise corrupting the security or
                functionality of the Website, the User may also be
                subject to civil and criminal liability.
              </p>
            </section>

            {/* =========================
                Questions
            ========================= */}

            <section
              id="questions"
              className="privacy-section"
            >
              <h2>Questions?</h2>

              <p>
                If you have any questions regarding these Terms and
                Conditions, please contact us at:
              </p>

              <div className="privacy-contact-grid">
                <div>
                  <span>SUPPORT</span>

                  <a href="mailto:support@xyncely.com">
                    support@xyncely.com
                  </a>

                  <small>
                    For questions regarding these Terms
                  </small>
                </div>
              </div>
            </section>

          </article>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Terms;